import React from 'react';
import { ArrowRight, Star } from 'lucide-react';
import { clinicInfo, heroImages } from '../mockData';
import { openWhatsApp } from '@/lib/whatsapp';

const HeroSection = () => {
  const handleWhatsAppClick = () => {
    openWhatsApp({
      section: 'hero',
      message: 'Olá! Vim pelo site e quero agendar uma avaliação sem compromisso.',
    });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16 md:pb-24">
      <div
        className="absolute inset-0 z-0 bg-gradient-to-br from-brand-cream via-[#f0f2f6] to-brand-wash"
        aria-hidden
      />
      <div className="absolute inset-0 z-0 opacity-[0.07]" aria-hidden>
        <div className="absolute -top-24 -right-24 h-[520px] w-[520px] rounded-full bg-brand-deep blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-96 w-96 rounded-full bg-brand-steel blur-3xl" />
      </div>
      <div
        className="pointer-events-none absolute inset-0 z-0 bg-[linear-gradient(105deg,transparent_0%,rgba(247,245,242,0.92)_42%,rgba(247,245,242,0.65)_100%)] md:bg-[linear-gradient(95deg,transparent_0%,rgba(247,245,242,0.88)_38%,transparent_72%)]"
        aria-hidden
      />

      <div className="container relative z-10 mx-auto px-4">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="space-y-10 lg:col-span-6">
            <div className="inline-flex max-w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-full border border-brand-ink/10 bg-white/70 px-5 py-2.5 shadow-sm backdrop-blur-md">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-brand-ink">
                <Star className="h-4 w-4 fill-brand-gold text-brand-gold" strokeWidth={0} />
                {clinicInfo.rating} no Google
              </span>
              <span className="hidden h-4 w-px bg-brand-ink/15 sm:block" aria-hidden />
              <span className="text-sm font-medium text-brand-muted">
                Ortodontia e odontologia em Paranaguá
              </span>
            </div>

            <div>
              <p className="mb-4 text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-brand-steel">
                Clínica em Paranaguá
              </p>
              <h1 className="font-display text-[2.65rem] font-semibold leading-[1.08] tracking-tight text-brand-ink sm:text-5xl md:text-6xl lg:text-[3.5rem]">
                Seu sorriso bonito e saudável, com quem cuida de{' '}
                <span className="relative">
                  <span className="relative z-10 italic text-brand-deep">Paranaguá</span>
                  <span
                    className="absolute -bottom-1 left-0 right-0 h-3 bg-brand-gold/25"
                    aria-hidden
                  />
                </span>{' '}
                há mais de {clinicInfo.experience}
              </h1>
            </div>

            <p className="max-w-xl text-lg font-light leading-relaxed text-brand-muted md:text-xl">
              Aparelho, implante e tratamentos para toda a família. Avaliação pelo WhatsApp, com
              parcelamento facilitado e atendimento acolhedor.
            </p>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={handleWhatsAppClick}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-deep px-8 py-4 text-base font-semibold text-white shadow-brand-lg transition hover:bg-brand-ink hover:shadow-brand-xl"
              >
                Avaliação sem compromisso
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                type="button"
                onClick={() =>
                  document.getElementById('tratamentos')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="inline-flex items-center justify-center rounded-full border border-brand-ink/12 bg-white/80 px-8 py-4 text-base font-semibold text-brand-ink shadow-sm backdrop-blur-sm transition hover:border-brand-deep/25 hover:bg-white"
              >
                Ver tratamentos
              </button>
            </div>

            <div className="grid grid-cols-3 gap-6 border-t border-brand-ink/10 pt-10 sm:max-w-lg">
              {[
                { value: clinicInfo.experience, label: 'de experiência' },
                { value: '1000+', label: 'pacientes' },
                { value: '5.0', label: 'no Google' },
              ].map((item) => (
                <div key={item.label} className="text-center sm:text-left">
                  <div className="font-display text-3xl font-semibold text-brand-deep md:text-4xl">
                    {item.value}
                  </div>
                  <div className="mt-1 text-xs font-medium uppercase tracking-wider text-brand-muted">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative lg:col-span-6">
            <div className="relative mx-auto max-w-lg lg:max-w-none">
              <div
                className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-brand-gold/20 via-transparent to-brand-deep/10 blur-2xl"
                aria-hidden
              />
              <div className="relative overflow-hidden rounded-[1.75rem] shadow-brand-xl ring-1 ring-white/80">
                <img
                  src={heroImages.hero}
                  alt="Clínica Ortodontia Líder"
                  className="h-[min(72vh,640px)] w-full object-cover md:h-[600px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/35 via-transparent to-transparent" />
              </div>

              <div className="absolute -bottom-5 left-4 right-4 sm:-left-5 sm:right-auto sm:max-w-xs">
                <div className="rounded-2xl border border-white/60 bg-white/90 p-5 shadow-brand-lg backdrop-blur-md">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-wash ring-1 ring-brand-ink/5">
                      <Star className="h-7 w-7 fill-brand-gold text-brand-gold" strokeWidth={0} />
                    </div>
                    <div>
                      <div className="font-display text-3xl font-semibold text-brand-ink">5.0</div>
                      <p className="text-sm font-medium text-brand-muted">Avaliação no Google</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
